// Shared month/year filter + CSV-export helpers used by the technician
// Allocations, Products and Tasks pages. Kept dependency-free so it can be
// imported from any page without pulling in extra state.

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// "2026-08-15T..." / "2026-08-15 10:00:00" / Date -> "2026-08"
// Uses the browser's local timezone, matching how these pages already
// render dates (formatDate/fmtDate use `new Date(x).toLocaleDateString()`),
// so the month a record is filtered/exported into always matches what is
// shown on screen for that same record.
export function getMonthKey(value) {
  if (!value) return null;

  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return null;

  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

// "2026-08" -> "Aug 2026"
export function monthKeyLabel(monthKey) {
  if (!monthKey || monthKey === "ALL") return "All Months";

  const [year, month] = monthKey.split("-");
  const idx = Number(month) - 1;

  if (!year || idx < 0 || idx > 11) return monthKey;

  return `${MONTH_NAMES[idx].slice(0, 3)} ${year}`;
}

// Builds a sorted (newest first) list of { value, label } options for the
// month <select>, derived from the actual dates present in `rows` — this
// keeps the picker accurate without hardcoding any particular year.
export function buildMonthOptions(rows, dateField) {
  const keys = new Set();

  for (const row of rows) {
    const key = getMonthKey(row?.[dateField]);
    if (key) keys.add(key);
  }

  return [...keys]
    .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
    .map((key) => ({ value: key, label: monthKeyLabel(key) }));
}

// Suffix for export filenames: "2026-08" -> "2026-08", "ALL" -> "all".
export function monthFileSuffix(monthKey) {
  return !monthKey || monthKey === "ALL" ? "all" : monthKey;
}

// Removes characters that are unsafe in a filename.
export function sanitizeFileNamePart(value) {
  return String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "unit";
}

// Guards against CSV formula injection: a cell starting with =, +, -, @
// (or tab/CR) is interpreted as a formula by Excel/Sheets when opened, so
// such values are prefixed with a leading apostrophe to force text mode.
export function sanitizeCsvCell(value) {
  const s = String(value ?? "");
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

// Wraps a (already formula-sanitized) value in quotes for a CSV row, per
// the existing quoting convention used across these pages.
export function csvQuote(value) {
  return `"${sanitizeCsvCell(value).replace(/"/g, '""')}"`;
}
